import { useMemo } from "react";
import { Smartphone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fmtInt } from "@/lib/format";

export default function RankingApps({ clientes = [] }) {
  const data = useMemo(() => {
    // Agrupa por vendedor -> apps -> count
    const byVendor = new Map();
    for (const c of clientes) {
      const v = (c.vendedor || "").trim() || "Sem vendedor";
      const app = (c.app_escolhido || "").trim() || "—";
      if (!byVendor.has(v)) byVendor.set(v, { total: 0, apps: new Map() });
      const acc = byVendor.get(v);
      acc.total += 1;
      acc.apps.set(app, (acc.apps.get(app) || 0) + 1);
    }
    return [...byVendor.entries()]
      .map(([nome, v]) => {
        const apps = [...v.apps.entries()].sort((a, b) => b[1] - a[1]);
        return { nome, total: v.total, apps };
      })
      .sort((a, b) => b.total - a.total);
  }, [clientes]);

  const globalTop = useMemo(() => {
    const counter = {};
    for (const c of clientes) {
      const app = (c.app_escolhido || "").trim();
      if (!app) continue;
      counter[app] = (counter[app] || 0) + 1;
    }
    return Object.entries(counter).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [clientes]);

  const maxGlobal = globalTop[0]?.[1] || 1;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="ranking-apps">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Ranking de apps</h3>
        </div>
        <Badge variant="outline">Por vendedor</Badge>
      </div>

      {/* Top global */}
      {globalTop.length > 0 && (
        <div className="mb-5 rounded-lg border border-border bg-background p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Top 5 no time</div>
          <div className="space-y-2">
            {globalTop.map(([app, count]) => (
              <div key={app} className="flex items-center gap-3">
                <div className="text-sm font-medium min-w-[120px] truncate">{app}</div>
                <Progress value={(count / maxGlobal) * 100} className="h-2 flex-1" />
                <div className="text-xs font-semibold tabular-nums w-14 text-right text-primary">{fmtInt(count)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Por vendedor */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.length === 0 && <div className="text-sm text-muted-foreground">Sem apps cadastrados no período.</div>}
        {data.map((v) => (
          <div key={v.nome} className="rounded-lg border border-border p-3" data-testid={`apps-vendedor-${v.nome}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-semibold text-sm truncate">{v.nome}</div>
              <Badge variant="outline" className="text-[10px]">{fmtInt(v.total)} vendas</Badge>
            </div>
            <div className="space-y-1.5">
              {v.apps.slice(0, 4).map(([app, count]) => (
                <div key={app} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground truncate max-w-[70%]">{app}</span>
                  <span className="tabular-nums font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
